import React, { useEffect, useRef, useState } from 'react';

import { bemCssModules, correctingTrailingNumbers } from '@cryptoboyio/mercuryo-lab/utils';

import { getTestId } from '@utils';
import { useCurrencyBase } from '@utils/hooks';

import { notificationsActions, useStore } from '@modules';
import { CryptoCurrencyEnum, CurrencyEnum, FiatCurrencyEnum } from '@modules/global/types';
import { NotificationTypesEnum } from '@modules/notifications/types';
import { WalletLimitsType } from '@modules/wallet/types';

import MoneyInput from '@components/MoneyInput';
import { useCustomContext } from '@containers/OperationBlock/context';

import useConvert, { GetRate } from './useConvert';
import useConverterState from './useConverterState';

import style from './Converter.scss';

const cn = bemCssModules(style, 'Converter');
const ti = getTestId('Converter');
const getOptions = (array: CurrencyEnum[]) => array.map(item => ({ value: item, label: item }));

const CLEAR_ERROR_TIMEOUT = 3000;

export type { ErrorType } from './useConvert';

export type ConverterDataType = {
  [k in 'from' | 'to']: {
    amount: string;
    currency: CurrencyEnum;
  };
} & { inputCurrency: CurrencyEnum };

export type TConverter = {
  onChange?: (data: ConverterDataType) => void;
  getRate?: GetRate;
  isLoading?: boolean;
  disabled?: boolean;
  limits?: WalletLimitsType;
  paramsRequireRateUpdate?: React.DependencyList;
} & ReturnType<typeof useConverterState>;

export const Converter: CustomFC<TConverter> = props => {
  const {
    className,
    onChange,
    getRate,
    disabled,
    paramsRequireRateUpdate = [],
    options,
    fromAmount,
    setFromAmount,
    fromCurrency,
    setFromCurrency,
    toAmount,
    setToAmount,
    toCurrency,
    setToCurrency,
    rate,
    setRate,
    setFocusedCurrency,
    setShowLimits,
    fromLimit,
    setFromLimit,
    toLimit,
    setToLimit,
    inputCurrency,
    setInputCurrency,
  } = props;

  const {
    setSelectedCurrency,
    setFiatCurrency,
    setFromCurrency: setContextFromCurrency,
    setToCurrency: setContextToCurrency,
  } = useCustomContext();
  const [store, dispatch] = useStore();
  const { display_options } = store.global.currencies.config;

  const fromCurrencies = getOptions(options.fromCurrencies);
  const toCurrencies = getOptions(options.toCurrencies);

  const [isFromAmountPristine, setIsFromAmountPristine] = useState(!fromAmount);
  const [isToAmountPristine, setIsToAmountPristine] = useState(!toAmount);

  const [isLocalFetching, setIsLocalFetching] = useState(false);

  const [convert, error, cancel] = useConvert(getRate, setRate, setIsLocalFetching);
  const { getBaseNetwork } = useCurrencyBase();

  const isFiat = (cur: FiatCurrencyEnum | CryptoCurrencyEnum): cur is FiatCurrencyEnum =>
    store.global.currencies.fiat.some(cryptoCurrency => cur === cryptoCurrency);

  const convertAction = () => {
    if (inputCurrency === fromCurrency) convert(fromCurrency, toCurrency, fromAmount);
    else convert(toCurrency, fromCurrency, toAmount);
    if (isFiat(toCurrency)) setFiatCurrency(toCurrency);
    else if (isFiat(fromCurrency)) setFiatCurrency(fromCurrency);
  };

  useEffect(() => {
    if (paramsRequireRateUpdate) convertAction();
  }, paramsRequireRateUpdate);

  const fromError = inputCurrency === fromCurrency && error;
  const toError = inputCurrency === toCurrency && error;

  useEffect(() => {
    if (fromAmount) setInputCurrency(fromCurrency);
  }, [fromCurrency]);

  useEffect(() => {
    if (toAmount) setInputCurrency(toCurrency);
  }, [toCurrency]);

  useEffect(() => {
    const message = fromError || toError;
    const clear = () => {
      dispatch(notificationsActions.clear());
    };
    if (typeof message === 'string') {
      dispatch(notificationsActions.add({ message, type: NotificationTypesEnum.error }));
    }
    if (fromError) setToAmount('');
    if (toError) setFromAmount('');
    const timeout = setTimeout(clear, CLEAR_ERROR_TIMEOUT);
    return () => {
      clearTimeout(timeout);
    };
  }, [fromError, toError]);

  useEffect(() => {
    const correctingFromAmount = correctingTrailingNumbers(
      rate?.total?.[fromCurrency] || fromAmount,
      display_options?.[fromCurrency]?.display_digits,
      display_options?.[fromCurrency]?.total_digits
    );
    const correctingToAmount = correctingTrailingNumbers(
      rate?.subtotal?.[toCurrency] || toAmount,
      display_options?.[toCurrency]?.display_digits,
      display_options?.[toCurrency]?.total_digits
    );

    setIsFromAmountPristine(true);
    setIsToAmountPristine(true);
    setFromAmount(correctingFromAmount);
    setToAmount(correctingToAmount);
  }, [rate]);

  useEffect(() => {
    if (!isFromAmountPristine || !isToAmountPristine) {
      setIsLocalFetching(+fromAmount > 0 || +toAmount > 0);
      if (onChange) {
        onChange({
          from: { amount: fromAmount, currency: fromCurrency },
          to: { amount: toAmount, currency: toCurrency },
          inputCurrency,
        });
      }
      convertAction();
    }
  }, [
    fromAmount,
    fromCurrency,
    toAmount,
    toCurrency,
    inputCurrency,
    isFromAmountPristine,
    isToAmountPristine,
  ]);

  const onChangeFromAmount = (value: string) => {
    if (inputCurrency !== fromCurrency) setInputCurrency(fromCurrency);
    setFromAmount(value);
    if (!value || +value <= 0) {
      setToAmount('');
      setIsToAmountPristine(true);
      cancel();
    }
    setFromLimit('');
    setIsFromAmountPristine(false);
  };

  const onChangeFromCurrency = value => {
    if (isFiat(value)) setFiatCurrency(value);

    setContextFromCurrency(value);
    setSelectedCurrency(value);

    if (inputCurrency === fromCurrency) setInputCurrency(value);
    setFromCurrency(value);
    setIsFromAmountPristine(false);
  };

  const onChangeToAmount = (value: string) => {
    if (inputCurrency !== toCurrency) setInputCurrency(toCurrency);
    setToAmount(value);
    if (!value || +value <= 0) {
      setFromAmount('');
      setIsFromAmountPristine(true);
      cancel();
    }
    setToLimit('');
    setIsToAmountPristine(false);
  };

  const onChangeToCurrency = value => {
    if (isFiat(value)) setFiatCurrency(value);

    setContextToCurrency(value);
    setSelectedCurrency(value);

    if (inputCurrency === toCurrency) setInputCurrency(value);
    setToCurrency(value);
    setIsToAmountPristine(false);
  };

  useEffect(() => {
    if (fromLimit) onChangeFromAmount(fromLimit);
  }, [fromLimit]);

  useEffect(() => {
    if (toLimit) onChangeToAmount(toLimit);
  }, [toLimit]);

  const getTotalDigits = (currency: CurrencyEnum) => display_options?.[currency]?.total_digits;
  const getDisplayDigits = (currency: CurrencyEnum) => display_options?.[currency]?.display_digits;

  const leftInputRef = useRef<HTMLInputElement>(null);
  const rightInputRef = useRef<HTMLInputElement>(null);

  const onFocus = (value: CurrencyEnum) => {
    setSelectedCurrency(value);
    setShowLimits(true);
    setFocusedCurrency(value);
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.relatedTarget !== rightInputRef.current && e.relatedTarget !== leftInputRef.current) {
      setTimeout(() => setShowLimits(false), 100);
    }
  };

  return (
    <div className={cn(null, [className])} data-testid={ti()}>
      <MoneyInput
        ref={leftInputRef}
        currency={fromCurrency}
        onChange={onChangeFromAmount}
        value={fromAmount}
        currencyNetwork={getBaseNetwork(fromCurrency as CryptoCurrencyEnum)}
        totalDigits={getTotalDigits(fromCurrency)}
        displayDigits={getDisplayDigits(fromCurrency)}
        disabled={disabled}
        error={!!fromError}
        currencies={fromCurrencies}
        onCurrencyChange={onChangeFromCurrency}
        disabledChangeCurrency={disabled}
        currencySelectIsLoading={isLocalFetching}
        onFocus={() => onFocus(fromCurrency)}
        onBlur={onBlur}
        testId={ti('from')}
      />
      <MoneyInput
        ref={rightInputRef}
        currency={toCurrency}
        onChange={onChangeToAmount}
        value={toAmount}
        currencyNetwork={getBaseNetwork(toCurrency as CryptoCurrencyEnum)}
        totalDigits={getTotalDigits(toCurrency)}
        displayDigits={getDisplayDigits(toCurrency)}
        disabled={disabled}
        error={!!toError}
        currencies={toCurrencies}
        onCurrencyChange={onChangeToCurrency}
        disabledChangeCurrency={disabled}
        currencySelectIsLoading={isLocalFetching}
        onFocus={() => onFocus(toCurrency)}
        onBlur={onBlur}
        testId={ti('to')}
      />
    </div>
  );
};

export default Converter;
